import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "@/lib/api";
import { X, MapPin, ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import InquiryDialog from "@/components/InquiryDialog";

export default function Destinations() {
  const [selectedDestination, setSelectedDestination] = useState(null);

  const {
    data: items = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["dests"],
    queryFn: () =>
      api
        .get("/destinations", {
          params: {
            is_active: true,
            limit: 60,
          },
        })
        .then((r) => r.data.items),
  });

  const closeDetails = () => {
    setSelectedDestination(null);
  };

  return (
    <>
      {/* ================= DESTINATIONS PAGE ================= */}
      <div className="pt-32 pb-24 bg-slate-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">

          {/* HEADER */}
          <div className="text-xs uppercase tracking-[0.24em] text-amber-600 mb-4">
            Destinations
          </div>

          <h1 className="font-display text-5xl sm:text-6xl text-slate-950 leading-none">
            The places we love.
          </h1>

          <p className="mt-5 max-w-2xl text-slate-600 text-base leading-relaxed">
            Discover carefully selected destinations and travel comfortably
            with Vizag Jagan Tours & Travels.
          </p>

          {/* LOADING */}
          {isLoading && (
            <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((item) => (
                <div
                  key={item}
                  className="rounded-2xl overflow-hidden bg-white border border-slate-200 animate-pulse"
                >
                  <div className="aspect-[4/3] bg-slate-200" />
                  <div className="p-6 space-y-4">
                    <div className="h-3 w-24 bg-slate-200 rounded" />
                    <div className="h-7 w-40 bg-slate-200 rounded" />
                    <div className="h-4 w-full bg-slate-200 rounded" />
                    <div className="h-4 w-3/4 bg-slate-200 rounded" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ERROR */}
          {isError && (
            <div className="mt-14 rounded-2xl border border-red-200 bg-red-50 p-8 text-center">
              <h2 className="font-display text-2xl text-slate-900">
                Unable to load destinations
              </h2>

              <p className="mt-2 text-slate-600">
                Please try again later.
              </p>
            </div>
          )}

          {/* DESTINATION CARDS */}
          {!isLoading && !isError && (
            <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((d) => (
                <article
                  key={d.id}
                  className="group rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-sm hover:shadow-xl transition-shadow duration-300"
                  data-testid={`dest-card-${d.slug}`}
                >
                  {/* IMAGE */}
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <img
                      src={d.image_url}
                      alt={d.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />

                    {/* IMAGE OVERLAY */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                    {/* VIEW DETAILS */}
                    <button
                      type="button"
                      onClick={() => setSelectedDestination(d)}
                      className="absolute bottom-5 left-5 right-5 bg-white text-slate-950 rounded-full py-3 px-5 font-semibold text-sm flex items-center justify-center gap-2 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 shadow-lg"
                    >
                      View Details
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>

                  {/* CARD CONTENT */}
                  <div className="p-6">
                    <div className="text-xs uppercase tracking-[0.2em] text-amber-600">
                      {d.region}
                    </div>

                    <div className="mt-2 font-display text-2xl text-slate-950">
                      {d.name}
                    </div>

                    <p className="text-slate-600 text-sm mt-2 leading-relaxed">
                      {d.description}
                    </p>

                    {/* HIGHLIGHTS */}
                    {d.highlights?.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {d.highlights.map((h, i) => (
                          <span
                            key={i}
                            className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-700"
                          >
                            {h}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* MOBILE VIEW DETAILS */}
                    <button
                      type="button"
                      onClick={() => setSelectedDestination(d)}
                      className="mt-5 lg:hidden w-full rounded-full border border-slate-300 py-3 px-5 text-sm font-semibold text-slate-900 flex items-center justify-center gap-2 hover:bg-slate-50 transition-colors"
                    >
                      View Details
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}

          {/* NO DESTINATIONS */}
          {!isLoading && !isError && items.length === 0 && (
            <div className="mt-14 text-center py-20">
              <h2 className="font-display text-3xl text-slate-900">
                No destinations available
              </h2>

              <p className="mt-3 text-slate-600">
                Please check back soon for our popular destinations.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ================= DESTINATION DETAILS MODAL ================= */}
      {selectedDestination && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeDetails}
        >
          <div
            className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-3xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* CLOSE BUTTON */}
            <button
              type="button"
              onClick={closeDetails}
              className="absolute right-5 top-5 z-20 w-10 h-10 rounded-full bg-white/95 shadow-lg grid place-items-center text-slate-900 hover:bg-slate-100 transition-colors"
              aria-label="Close destination details"
            >
              <X className="w-5 h-5" />
            </button>

            {/* LARGE IMAGE */}
            <div className="w-full aspect-[16/8] overflow-hidden rounded-t-3xl">
              <img
                src={selectedDestination.image_url}
                alt={selectedDestination.name}
                className="w-full h-full object-cover"
              />
            </div>

            {/* DETAILS */}
            <div className="p-6 sm:p-8 lg:p-10">
              <div className="text-xs uppercase tracking-[0.24em] text-amber-600">
                {selectedDestination.region}
              </div>

              <h2 className="mt-3 font-display text-4xl sm:text-5xl text-slate-950">
                {selectedDestination.name}
              </h2>

              <p className="mt-5 text-slate-600 leading-relaxed text-base">
                {selectedDestination.description}
              </p>

              {/* HIGHLIGHTS */}
              {selectedDestination.highlights?.length > 0 && (
                <div className="mt-7">
                  <h3 className="text-sm uppercase tracking-[0.2em] text-slate-900 font-semibold">
                    Highlights
                  </h3>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {selectedDestination.highlights.map((h, i) => (
                      <span
                        key={i}
                        className="px-4 py-2 rounded-full bg-slate-100 text-slate-700 text-sm"
                      >
                        {h}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* LOCATION */}
              <div className="mt-8 flex items-start gap-3 text-slate-600">
                <MapPin className="w-5 h-5 mt-0.5 text-amber-600 shrink-0" />

                <div>
                  <div className="font-semibold text-slate-900">
                    Explore {selectedDestination.name}
                  </div>

                  <div className="text-sm mt-1">
                    Plan your journey with comfortable transportation and
                    professional travel services.
                  </div>
                </div>
              </div>

              {/* ACTIONS */}
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <InquiryDialog
                  defaultMessage={`Hello! I would like to plan a trip to ${selectedDestination.name}. Please share the available packages and best options.`}
                  source={`destination_${selectedDestination.slug}`}
                  triggerTestId={`destination-book-${selectedDestination.slug}`}
                >
                  <Button
                    className="w-full sm:w-auto rounded-full bg-brand-navy-900 hover:bg-brand-navy-800 text-white px-7 py-3"
                  >
                    Book This Journey
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </InquiryDialog>

                <a
                  href="tel:"
                  className="w-full sm:w-auto rounded-full border border-slate-300 px-7 py-3 text-sm font-semibold text-slate-900 flex items-center justify-center gap-2 hover:bg-slate-50 transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  Call Now
                </a>

                <button
                  type="button"
                  onClick={closeDetails}
                  className="w-full sm:w-auto rounded-full border border-slate-300 px-7 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}